/** Default home banner background when no custom media is uploaded. */
export const DEFAULT_BANNER_VIDEO = `${process.env.PUBLIC_URL}/ezgif-5d97791190fa5f40.mp4`;

/** Default home banner mascot when no custom mascot is uploaded. */
export const DEFAULT_BANNER_MASCOT = `${process.env.PUBLIC_URL}/hero-mascot.png`;

export const DEFAULT_OVERLAY_LINE1_COLOR = '#ffffff';
export const DEFAULT_OVERLAY_LINE2_COLOR = '#ffffff';

export const DEFAULT_OVERLAY_FONT = '"Brush Script MT", "Segoe Script", cursive';

export const HERO_BANNER_OVERLAY_FONT_OPTIONS: { value: string; label: string }[] = [
  { value: DEFAULT_OVERLAY_FONT, label: 'Cursive (default)' },
  { value: 'cursive', label: 'Generic cursive' },
  { value: '"Palatino Linotype", "Book Antiqua", Palatino, serif', label: 'Palatino' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Georgia' },
  { value: '"Noto Sans Sinhala", sans-serif', label: 'Noto Sans Sinhala' },
  { value: 'system-ui, -apple-system, "Segoe UI", sans-serif', label: 'System UI' },
];

/** True when admin has saved at least one overlay text line (otherwise use Vesak PNGs). */
export function hasCustomOverlayText(
  line1: string | null | undefined,
  line2: string | null | undefined,
): boolean {
  return !!(line1?.trim() || line2?.trim());
}

export function resolveOverlayFont(value: string | null | undefined): string {
  const t = value?.trim();
  return t || DEFAULT_OVERLAY_FONT;
}

export function resolveOverlayLineColor(
  value: string | null | undefined,
  fallback: string,
): string {
  const t = value?.trim();
  return t && /^#[0-9A-Fa-f]{6}$/.test(t) ? t : fallback;
}

export interface HeroBannerLayout {
  showMascot: boolean;
  showOverlay: boolean;
  showAnnouncementCard: boolean;
  mascotPosX: number;
  mascotPosY: number;
  overlayPosX: number;
  overlayPosY: number;
  announcementPosX: number;
  announcementPosY: number;
  mascotScale: number;
  overlayLine1Scale: number;
  overlayLine2Scale: number;
}

export const MIN_BANNER_ELEMENT_SCALE = 50;
export const MAX_BANNER_ELEMENT_SCALE = 200;

export const OVERLAY_LINE1_BASE_REM = 1.75;
export const OVERLAY_LINE2_BASE_REM = 1.25;

export const DEFAULT_BANNER_LAYOUT: HeroBannerLayout = {
  showMascot: true,
  showOverlay: true,
  showAnnouncementCard: true,
  mascotPosX: 50,
  mascotPosY: 0,
  overlayPosX: 5,
  overlayPosY: 7,
  announcementPosX: 3,
  announcementPosY: 6,
  mascotScale: 100,
  overlayLine1Scale: 100,
  overlayLine2Scale: 100,
};

export function clampLayoutPercent(value: number | null | undefined, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, Math.round(n)));
}

export function clampLayoutScale(value: number | null | undefined, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(MAX_BANNER_ELEMENT_SCALE, Math.max(MIN_BANNER_ELEMENT_SCALE, Math.round(n)));
}

export function scaledRem(baseRem: number, scale: number): string {
  return `${(baseRem * scale) / 100}rem`;
}

export function scaledPx(basePx: number, scale: number): string {
  return `${Math.round((basePx * scale) / 100)}px`;
}

export function resolveBannerLayout(
  partial: Partial<HeroBannerLayout> | null | undefined,
): HeroBannerLayout {
  const d = DEFAULT_BANNER_LAYOUT;
  return {
    showMascot: partial?.showMascot ?? d.showMascot,
    showOverlay: partial?.showOverlay ?? d.showOverlay,
    showAnnouncementCard: partial?.showAnnouncementCard ?? d.showAnnouncementCard,
    mascotPosX: clampLayoutPercent(partial?.mascotPosX, d.mascotPosX),
    mascotPosY: clampLayoutPercent(partial?.mascotPosY, d.mascotPosY),
    overlayPosX: clampLayoutPercent(partial?.overlayPosX, d.overlayPosX),
    overlayPosY: clampLayoutPercent(partial?.overlayPosY, d.overlayPosY),
    announcementPosX: clampLayoutPercent(partial?.announcementPosX, d.announcementPosX),
    announcementPosY: clampLayoutPercent(partial?.announcementPosY, d.announcementPosY),
    mascotScale: clampLayoutScale(partial?.mascotScale, d.mascotScale),
    overlayLine1Scale: clampLayoutScale(partial?.overlayLine1Scale, d.overlayLine1Scale),
    overlayLine2Scale: clampLayoutScale(partial?.overlayLine2Scale, d.overlayLine2Scale),
  };
}
